import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Save, MapPin, Phone, FileText, Calendar, Hash, Upload, Stamp, GraduationCap, Clock, Download, Trash2, CreditCard, Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const ProfilCentre = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [sites, setSites] = useState([]);
  const [showNewSiteForm, setShowNewSiteForm] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  
  // Template pour un nouveau profil vide
  const getEmptyProfile = (siteName) => ({
    site_id: `site_${Date.now()}`,
    site_name: siteName,
    nom_organisme: `Formation Internationale aux Sports de Loisirs - ${siteName}`,
    raison_sociale: `SARL Formation Internationale aux Sports de Loisirs (FISL)`,
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    gerant_nom: '',
    siret: '',
    numero_activite: '',
    numero_uai: '',
    numero_habilitation: '',
    code_ape: '',
    n_rncp: '',
    code_nsf: '335',
    formacode: '15472',
    code_diplome: '44633540',
    intitule_formation: 'BPJEPS Motonautisme et Disciplines Associées',
    specialite: 'éducateur sportif',
    bpjeps_abrev: 'MDA',
    centre_abrev: siteName.substring(0, 3).toUpperCase(),
    iban: '',
    // Blocs de compétences avec noms personnalisables
    nom_bc1: 'Concevoir et mettre en œuvre des projets d\'animation dans le cadre de l\'organisation de travail d\'une structure du champ du sport ou de l\'animation',
    nom_bc2: 'Valoriser les activités et les projets d\'une structure du sport ou de l\'animation',
    nom_bc3: 'Concevoir, conduire en sécurité et évaluer des séances et des cycles de découverte, d\'initiation et d\'apprentissage des activités de motonautisme et des disciplines associées',
    heures_bc1: '',
    heures_bc2: '',
    heures_bc3: '',
    heures_foad: '',
    duree_totale_centre: '',
    duree_totale_stage: '',
    // Tarification
    taux_horaire_formation: '',
    taux_horaire_apprentissage: '',
    montant_formation: '',
    depot_arrhes: '500',
    // Formateurs
    formateurs: [],
    // Informations juridiques
    region_prefecture: 'Nouvelle-Aquitaine',
    tribunal_competent: 'Saintes',
    periode_formation: '',
    google_calendar_code: '',
    session: '',
    date_debut_formation: '',
    date_fin_formation: '',
    horaires_matin: '09:00 - 12:00',
    horaires_apres_midi: '14:00 - 17:00',
    tampon_img: null,
    programme_pdf: null,
    programme_pdf_name: '',
    // Dates des tests de sélection (2 sessions)
    date_tests_1: '',
    label_tests_1: '',
    date_tests_2: '',
    label_tests_2: ''
  });

  // Ref pour l'upload du programme PDF
  const programmeInputRef = useRef(null);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      const response = await axios.get('/centre/profiles');
      if (response.data && response.data.sites && response.data.sites.length > 0) {
        setSites(response.data.sites);
        setSelectedSiteId(response.data.sites[0].site_id);
      } else if (response.data && (response.data.Atlantique || response.data.Occitanie)) {
        // Migration depuis l'ancien format
        const migratedSites = [];
        if (response.data.Atlantique) {
          migratedSites.push({
            ...getEmptyProfile('Atlantique'),
            ...response.data.Atlantique,
            site_id: 'atlantique',
            site_name: 'Atlantique'
          });
        }
        if (response.data.Occitanie) {
          migratedSites.push({
            ...getEmptyProfile('Occitanie'),
            ...response.data.Occitanie,
            site_id: 'occitanie',
            site_name: 'Occitanie'
          });
        }
        setSites(migratedSites);
        if (migratedSites.length > 0) {
          setSelectedSiteId(migratedSites[0].site_id);
        }
      } else {
        // Initialiser avec les 2 sites par défaut
        const defaultSites = [
          { ...getEmptyProfile('Atlantique'), site_id: 'atlantique', site_name: 'Atlantique' },
          { ...getEmptyProfile('Occitanie'), site_id: 'occitanie', site_name: 'Occitanie' }
        ];
        setSites(defaultSites);
        setSelectedSiteId('atlantique');
      }
    } catch (error) {
      // En cas d'erreur, initialiser avec les valeurs par défaut
      const defaultSites = [
        { ...getEmptyProfile('Atlantique'), site_id: 'atlantique', site_name: 'Atlantique' },
        { ...getEmptyProfile('Occitanie'), site_id: 'occitanie', site_name: 'Occitanie' }
      ];
      setSites(defaultSites);
      setSelectedSiteId('atlantique');
    } finally {
      setLoading(false);
    }
  };

  const currentProfile = sites.find(s => s.site_id === selectedSiteId) || sites[0];

  const handleChange = (field, value) => {
    setSites(prev => prev.map(site => 
      site.site_id === selectedSiteId 
        ? { ...site, [field]: value }
        : site
    ));
  };

  const handleTamponUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image (PNG, JPG)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('tampon_img', reader.result);
      toast.success('Tampon/Signature chargé');
    };
    reader.readAsDataURL(file);
  };

  const handleProgrammePdfUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('programme_pdf', reader.result);
      handleChange('programme_pdf_name', file.name);
      toast.success('Programme PDF chargé');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveProgrammePdf = () => {
    handleChange('programme_pdf', null);
    handleChange('programme_pdf_name', '');
    if (programmeInputRef.current) {
      programmeInputRef.current.value = '';
    }
    toast.success('Programme PDF supprimé');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post('/centre/profiles', { sites });
      toast.success('Profils enregistrés avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSite = () => {
    if (!newSiteName.trim()) {
      toast.error('Veuillez entrer un nom pour le nouveau centre');
      return;
    }
    
    // Vérifier si le nom existe déjà
    if (sites.some(s => s.site_name.toLowerCase() === newSiteName.trim().toLowerCase())) {
      toast.error('Un centre avec ce nom existe déjà');
      return;
    }

    const newSite = getEmptyProfile(newSiteName.trim());
    setSites(prev => [...prev, newSite]);
    setSelectedSiteId(newSite.site_id);
    setShowNewSiteForm(false);
    setNewSiteName('');
    toast.success(`Centre "${newSiteName.trim()}" créé. N'oubliez pas d'enregistrer.`);
  };

  const handleDeleteSite = (siteId) => {
    if (sites.length <= 1) {
      toast.error('Vous devez conserver au moins un centre');
      return;
    }
    
    const siteToDelete = sites.find(s => s.site_id === siteId);
    if (!window.confirm(`Supprimer le centre "${siteToDelete?.site_name}" ? Cette action est irréversible après enregistrement.`)) {
      return;
    }

    setSites(prev => prev.filter(s => s.site_id !== siteId));
    if (selectedSiteId === siteId) {
      const remainingSites = sites.filter(s => s.site_id !== siteId);
      setSelectedSiteId(remainingSites[0]?.site_id);
    }
    toast.success(`Centre supprimé. N'oubliez pas d'enregistrer.`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-600">Erreur: Aucun profil trouvé</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profil-centre-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Profil Centre de Formation</h1>
          <p className="text-slate-600">Configurez les informations de vos sites de formation</p>
        </div>
      </div>

      <div className="flex justify-end mb-4">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
          data-testid="save-profile"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Sélecteur de site avec bouton Ajouter */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm mb-6">
        <Label className="text-sm font-medium text-slate-700 mb-2 block">Site de formation</Label>
        <div className="flex flex-wrap gap-2 items-center">
          {sites.map(site => (
            <div key={site.site_id} className="relative group">
              <button
                onClick={() => setSelectedSiteId(site.site_id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedSiteId === site.site_id 
                    ? 'bg-[#8DC63F] text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`site-${site.site_id}`}
              >
                FISL {site.site_name}
              </button>
              {sites.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSite(site.site_id);
                  }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-red-600"
                  title="Supprimer ce centre"
                  data-testid={`delete-site-${site.site_id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          
          {/* Bouton Ajouter nouveau centre */}
          {showNewSiteForm ? (
            <div className="flex items-center gap-2">
              <Input
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="Nom du nouveau centre"
                className="w-48"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSite();
                  if (e.key === 'Escape') {
                    setShowNewSiteForm(false);
                    setNewSiteName('');
                  }
                }}
                data-testid="new-site-input"
              />
              <Button
                onClick={handleAddSite}
                size="sm"
                className="bg-[#8DC63F] hover:bg-[#7db336]"
                data-testid="confirm-add-site"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => {
                  setShowNewSiteForm(false);
                  setNewSiteName('');
                }}
                size="sm"
                variant="outline"
                data-testid="cancel-add-site"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setShowNewSiteForm(true)}
              variant="outline"
              className="border-dashed border-2 border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10"
              data-testid="add-new-site-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un nouveau centre
            </Button>
          )}
        </div>
      </div>

      {/* Informations de l'organisme */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Informations de l'organisme</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="nom_organisme">Nom de l'organisme</Label>
            <Input
              id="nom_organisme"
              value={currentProfile.nom_organisme}
              onChange={(e) => handleChange('nom_organisme', e.target.value)}
              placeholder="Formation Internationale aux Sports de Loisirs"
              data-testid="input-nom-organisme"
            />
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="adresse">Adresse</Label>
            <Input
              id="adresse"
              value={currentProfile.adresse}
              onChange={(e) => handleChange('adresse', e.target.value)}
              placeholder="123 rue du Port"
              data-testid="input-adresse"
            />
          </div>
          
          <div>
            <Label htmlFor="code_postal">Code postal</Label>
            <Input
              id="code_postal"
              value={currentProfile.code_postal}
              onChange={(e) => handleChange('code_postal', e.target.value)}
              placeholder="17000"
              data-testid="input-code-postal"
            />
          </div>
          
          <div>
            <Label htmlFor="ville">Ville</Label>
            <Input
              id="ville"
              value={currentProfile.ville}
              onChange={(e) => handleChange('ville', e.target.value)}
              placeholder="La Rochelle"
              data-testid="input-ville"
            />
          </div>
          
          <div>
            <Label htmlFor="telephone">Téléphone</Label>
            <Input
              id="telephone"
              value={currentProfile.telephone}
              onChange={(e) => handleChange('telephone', e.target.value)}
              placeholder="05 46 00 00 00"
              data-testid="input-telephone"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={currentProfile.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@fisl.fr"
              data-testid="input-email"
            />
          </div>
          
          <div>
            <Label htmlFor="gerant_nom">Nom du gérant / Responsable</Label>
            <Input
              id="gerant_nom"
              value={currentProfile.gerant_nom || ''}
              onChange={(e) => handleChange('gerant_nom', e.target.value)}
              placeholder="Nom du gérant"
              data-testid="input-gerant-nom"
            />
          </div>
        </div>
      </div>

      {/* Numéros administratifs */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Hash className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Numéros administratifs</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="siret">SIRET</Label>
            <Input
              id="siret"
              value={currentProfile.siret}
              onChange={(e) => handleChange('siret', e.target.value)}
              placeholder="123 456 789 00012"
              data-testid="input-siret"
            />
          </div>
          
          <div>
            <Label htmlFor="numero_activite">Numéro d'activité</Label>
            <Input
              id="numero_activite"
              value={currentProfile.numero_activite}
              onChange={(e) => handleChange('numero_activite', e.target.value)}
              placeholder="75170123456"
              data-testid="input-numero-activite"
            />
          </div>
          
          <div>
            <Label htmlFor="numero_uai">Numéro UAI</Label>
            <Input
              id="numero_uai"
              value={currentProfile.numero_uai}
              onChange={(e) => handleChange('numero_uai', e.target.value)}
              placeholder="0171234A"
              data-testid="input-numero-uai"
            />
          </div>
          
          <div>
            <Label htmlFor="numero_habilitation">Numéro d'habilitation</Label>
            <Input
              id="numero_habilitation"
              value={currentProfile.numero_habilitation}
              onChange={(e) => handleChange('numero_habilitation', e.target.value)}
              placeholder="HAB-2024-001"
              data-testid="input-numero-habilitation"
            />
          </div>
          
          <div>
            <Label htmlFor="code_ape">Code APE</Label>
            <Input
              id="code_ape"
              value={currentProfile.code_ape || ''}
              onChange={(e) => handleChange('code_ape', e.target.value)}
              placeholder="8559A"
              data-testid="input-code-ape"
            />
          </div>
        </div>
      </div>

      {/* Informations de la formation */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Informations de la formation</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="n_rncp">N° RNCP</Label>
            <Input
              id="n_rncp"
              value={currentProfile.n_rncp || ''}
              onChange={(e) => handleChange('n_rncp', e.target.value)}
              placeholder="RNCP28573"
              data-testid="input-n-rncp"
            />
          </div>
          
          <div>
            <Label htmlFor="code_nsf">Code NSF</Label>
            <Input
              id="code_nsf"
              value={currentProfile.code_nsf || ''}
              onChange={(e) => handleChange('code_nsf', e.target.value)}
              placeholder="335"
              data-testid="input-code-nsf"
            />
          </div>

          <div>
            <Label htmlFor="formacode">Formacode</Label>
            <Input
              id="formacode"
              value={currentProfile.formacode || ''}
              onChange={(e) => handleChange('formacode', e.target.value)}
              placeholder="15472"
              data-testid="input-formacode"
            />
          </div>

          <div>
            <Label htmlFor="code_diplome">Code diplôme</Label>
            <Input
              id="code_diplome"
              value={currentProfile.code_diplome || ''}
              onChange={(e) => handleChange('code_diplome', e.target.value)}
              placeholder="44633540"
              data-testid="input-code-diplome"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="specialite">Spécialité</Label>
            <Input
              id="specialite"
              value={currentProfile.specialite}
              onChange={(e) => handleChange('specialite', e.target.value)}
              placeholder="Éducateur Sportif"
              data-testid="input-specialite"
            />
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="intitule_formation">Intitulé de la formation (Mention)</Label>
            <Input
              id="intitule_formation"
              value={currentProfile.intitule_formation}
              onChange={(e) => handleChange('intitule_formation', e.target.value)}
              placeholder="BPJEPS Motonautisme et Disciplines Associées"
              data-testid="input-intitule-formation"
            />
          </div>
          
          {/* Blocs de Compétences - Noms et Heures */}
          <div className="md:col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Blocs de Compétences (Noms + Heures pour PIF, Devis, Convention)
            </h4>
            <p className="text-xs text-blue-600 mb-3">Personnalisez les noms des BC et leurs heures. Ces valeurs seront utilisées dans tous les documents.</p>
            
            {/* BC1 */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div className="col-span-5">
                  <Label htmlFor="nom_bc1">BC1 - Intitulé</Label>
                  <Input
                    id="nom_bc1"
                    value={currentProfile.nom_bc1 || ''}
                    onChange={(e) => handleChange('nom_bc1', e.target.value)}
                    placeholder="Concevoir et mettre en œuvre des projets d'animation..."
                    data-testid="input-nom-bc1"
                  />
                </div>
                <div>
                  <Label htmlFor="heures_bc1">Heures</Label>
                  <Input
                    id="heures_bc1"
                    type="number"
                    value={currentProfile.heures_bc1 || ''}
                    onChange={(e) => handleChange('heures_bc1', e.target.value)}
                    placeholder="150"
                    data-testid="input-heures-bc1"
                  />
                </div>
              </div>
            </div>
            
            {/* BC2 */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div className="col-span-5">
                  <Label htmlFor="nom_bc2">BC2 - Intitulé</Label>
                  <Input
                    id="nom_bc2"
                    value={currentProfile.nom_bc2 || ''}
                    onChange={(e) => handleChange('nom_bc2', e.target.value)}
                    placeholder="Valoriser les activités et les projets..."
                    data-testid="input-nom-bc2"
                  />
                </div>
                <div>
                  <Label htmlFor="heures_bc2">Heures</Label>
                  <Input
                    id="heures_bc2"
                    type="number"
                    value={currentProfile.heures_bc2 || ''}
                    onChange={(e) => handleChange('heures_bc2', e.target.value)}
                    placeholder="100"
                    data-testid="input-heures-bc2"
                  />
                </div>
              </div>
            </div>
            
            {/* BC3 */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div className="col-span-5">
                  <Label htmlFor="nom_bc3">BC3 - Intitulé</Label>
                  <Input
                    id="nom_bc3"
                    value={currentProfile.nom_bc3 || ''}
                    onChange={(e) => handleChange('nom_bc3', e.target.value)}
                    placeholder="Concevoir, conduire en sécurité et évaluer..."
                    data-testid="input-nom-bc3"
                  />
                </div>
                <div>
                  <Label htmlFor="heures_bc3">Heures</Label>
                  <Input
                    id="heures_bc3"
                    type="number"
                    value={currentProfile.heures_bc3 || ''}
                    onChange={(e) => handleChange('heures_bc3', e.target.value)}
                    placeholder="200"
                    data-testid="input-heures-bc3"
                  />
                </div>
              </div>
            </div>
            
            {/* FOAD */}
            <div className="p-3 bg-white rounded-lg border border-blue-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div className="col-span-5">
                  <Label>FOAD (Formation Ouverte à Distance)</Label>
                  <p className="text-xs text-slate-500">Heures de formation à distance incluses dans le total</p>
                </div>
                <div>
                  <Label htmlFor="heures_foad">Heures</Label>
                  <Input
                    id="heures_foad"
                    type="number"
                    value={currentProfile.heures_foad || ''}
                    onChange={(e) => handleChange('heures_foad', e.target.value)}
                    placeholder="50"
                    data-testid="input-heures-foad"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Dates des tests de sélection */}
          <div className="md:col-span-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Dates des tests de sélection
            </h4>
            <p className="text-xs text-amber-600 mb-3">
              Ces 2 dates seront proposées aux candidats lors de leur inscription aux tests de sélection.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date 1 */}
              <div className="p-3 bg-white rounded-lg border border-amber-200">
                <Label className="text-amber-700 font-medium">Session 1</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <Input
                      type="date"
                      value={currentProfile.date_tests_1 || ''}
                      onChange={(e) => handleChange('date_tests_1', e.target.value)}
                      data-testid="input-date-tests-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Libellé</Label>
                    <Input
                      value={currentProfile.label_tests_1 || ''}
                      onChange={(e) => handleChange('label_tests_1', e.target.value)}
                      placeholder="Ex: Mars 2025"
                      data-testid="input-label-tests-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Date 2 */}
              <div className="p-3 bg-white rounded-lg border border-amber-200">
                <Label className="text-amber-700 font-medium">Session 2</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <Input
                      type="date"
                      value={currentProfile.date_tests_2 || ''}
                      onChange={(e) => handleChange('date_tests_2', e.target.value)}
                      data-testid="input-date-tests-2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Libellé</Label>
                    <Input
                      value={currentProfile.label_tests_2 || ''}
                      onChange={(e) => handleChange('label_tests_2', e.target.value)}
                      placeholder="Ex: Juin 2025"
                      data-testid="input-label-tests-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Durées de formation - CALCULÉES AUTOMATIQUEMENT */}
          <div className="md:col-span-2 p-4 bg-teal-50 rounded-lg border border-teal-200">
            <h4 className="font-medium text-teal-800 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Durées de formation (calculées automatiquement)
            </h4>
            <p className="text-xs text-teal-600 mb-3">La durée en centre = BC1 + BC2 + BC3. Le stage est saisi manuellement.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duree_totale_centre">Durée totale en centre de formation (h)</Label>
                <Input
                  id="duree_totale_centre"
                  type="number"
                  value={
                    (parseInt(currentProfile.heures_bc1 || 0) + 
                     parseInt(currentProfile.heures_bc2 || 0) + 
                     parseInt(currentProfile.heures_bc3 || 0) +
                     parseInt(currentProfile.heures_foad || 0)) || ''
                  }
                  readOnly
                  className="bg-teal-100 cursor-not-allowed"
                  data-testid="input-duree-totale-centre"
                />
                <p className="text-xs text-slate-500 mt-1">= BC1 ({currentProfile.heures_bc1 || 0}h) + BC2 ({currentProfile.heures_bc2 || 0}h) + BC3 ({currentProfile.heures_bc3 || 0}h) + FOAD ({currentProfile.heures_foad || 0}h)</p>
              </div>
              <div>
                <Label htmlFor="duree_totale_stage">Durée totale en stage (h)</Label>
                <Input
                  id="duree_totale_stage"
                  type="number"
                  value={currentProfile.duree_totale_stage || ''}
                  onChange={(e) => handleChange('duree_totale_stage', e.target.value)}
                  placeholder="ex: 496"
                  data-testid="input-duree-totale-stage"
                />
              </div>
            </div>
          </div>
          
          {/* Tarification - CALCULÉE AUTOMATIQUEMENT */}
          <div className="md:col-span-2 p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Tarification de la formation
            </h4>
            <p className="text-xs text-green-600 mb-3">Le montant total = Durée centre × Taux horaire.</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="taux_horaire_formation">Taux horaire (€/h)</Label>
                <Input
                  id="taux_horaire_formation"
                  type="number"
                  step="0.01"
                  value={currentProfile.taux_horaire_formation || ''}
                  onChange={(e) => handleChange('taux_horaire_formation', e.target.value)}
                  placeholder="ex: 9.50"
                  data-testid="input-taux-horaire"
                />
              </div>
              <div>
                <Label htmlFor="montant_formation">Montant total formation (€)</Label>
                <Input
                  id="montant_formation"
                  type="number"
                  value={
                    currentProfile.taux_horaire_formation 
                      ? Math.round((parseInt(currentProfile.heures_bc1 || 0) + 
                                    parseInt(currentProfile.heures_bc2 || 0) + 
                                    parseInt(currentProfile.heures_bc3 || 0) +
                                    parseInt(currentProfile.heures_foad || 0)) * 
                                   parseFloat(currentProfile.taux_horaire_formation || 0))
                      : ''
                  }
                  readOnly
                  className="bg-green-100 cursor-not-allowed"
                  data-testid="input-montant-formation"
                />
                <p className="text-xs text-slate-500 mt-1">= Durée centre × Taux horaire</p>
              </div>
              <div>
                <Label htmlFor="depot_arrhes">Dépôt arrhes (€)</Label>
                <Input
                  id="depot_arrhes"
                  type="number"
                  value={currentProfile.depot_arrhes || '500'}
                  onChange={(e) => handleChange('depot_arrhes', e.target.value)}
                  placeholder="ex: 500"
                  data-testid="input-depot-arrhes"
                />
              </div>
              <div>
                <Label htmlFor="taux_horaire_apprentissage">Taux horaire apprentissage (€/h)</Label>
                <Input
                  id="taux_horaire_apprentissage"
                  type="number"
                  step="0.01"
                  value={currentProfile.taux_horaire_apprentissage || ''}
                  onChange={(e) => handleChange('taux_horaire_apprentissage', e.target.value)}
                  placeholder="À renseigner"
                  data-testid="input-taux-horaire-apprentissage"
                />
                <p className="text-xs text-slate-500 mt-1">Proposé sur les devis, modifiable devis par devis</p>
              </div>
            </div>
          </div>
          
          {/* Formateurs */}
          <div className="md:col-span-2 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <h4 className="font-medium text-indigo-800 mb-2 flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Formateurs et leurs qualifications
            </h4>
            <p className="text-xs text-indigo-600 mb-3">Liste des formateurs qui apparaîtra dans la Convention de Mise en Situation Pédagogique.</p>
            
            {/* Liste des formateurs existants */}
            {(currentProfile.formateurs || []).map((formateur, index) => (
              <div key={index} className="mb-3 p-3 bg-white rounded-lg border border-indigo-100 flex gap-3 items-start">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nom du formateur</Label>
                    <Input
                      value={formateur.nom || ''}
                      onChange={(e) => {
                        const newFormateurs = [...(currentProfile.formateurs || [])];
                        newFormateurs[index] = { ...newFormateurs[index], nom: e.target.value };
                        handleChange('formateurs', newFormateurs);
                      }}
                      placeholder="Ex: Jean DUPONT"
                      data-testid={`input-formateur-nom-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Diplôme / Titre / Qualification</Label>
                    <Input
                      value={formateur.diplome || ''}
                      onChange={(e) => {
                        const newFormateurs = [...(currentProfile.formateurs || [])];
                        newFormateurs[index] = { ...newFormateurs[index], diplome: e.target.value };
                        handleChange('formateurs', newFormateurs);
                      }}
                      placeholder="Ex: BEES 2ème degré Motonautisme"
                      data-testid={`input-formateur-diplome-${index}`}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    const newFormateurs = (currentProfile.formateurs || []).filter((_, i) => i !== index);
                    handleChange('formateurs', newFormateurs);
                  }}
                  data-testid={`remove-formateur-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            {/* Bouton ajouter formateur */}
            <Button
              variant="outline"
              size="sm"
              className="border-dashed border-2 border-indigo-300 text-indigo-600 hover:bg-indigo-100"
              onClick={() => {
                const newFormateurs = [...(currentProfile.formateurs || []), { nom: '', diplome: '' }];
                handleChange('formateurs', newFormateurs);
              }}
              data-testid="add-formateur-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un formateur
            </Button>
          </div>
          
          {/* Informations juridiques */}
          <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Informations juridiques (Convention)
            </h4>
            <p className="text-xs text-slate-600 mb-3">Ces informations apparaissent dans la Convention de Mise en Situation Pédagogique.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="region_prefecture">Région / Préfecture</Label>
                <Input
                  id="region_prefecture"
                  value={currentProfile.region_prefecture || ''}
                  onChange={(e) => handleChange('region_prefecture', e.target.value)}
                  placeholder="Ex: Nouvelle-Aquitaine"
                  data-testid="input-region-prefecture"
                />
              </div>
              <div>
                <Label htmlFor="tribunal_competent">Tribunal compétent</Label>
                <Input
                  id="tribunal_competent"
                  value={currentProfile.tribunal_competent || ''}
                  onChange={(e) => handleChange('tribunal_competent', e.target.value)}
                  placeholder="Ex: Saintes"
                  data-testid="input-tribunal-competent"
                />
              </div>
            </div>
          </div>
          
          {/* Numérotation Devis/Factures */}
          <div className="md:col-span-2 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Numérotation Devis/Factures
            </h4>
            <p className="text-xs text-purple-600 mb-3">Format: DEV-[BPJEPS]-[CENTRE]-[ANNÉE]-[NUMÉRO] | Exemple: DEV-MDA-ATL-2026-0001</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bpjeps_abrev">Abréviation BPJEPS</Label>
                <Input
                  id="bpjeps_abrev"
                  type="text"
                  maxLength={5}
                  value={currentProfile.bpjeps_abrev || 'MDA'}
                  onChange={(e) => handleChange('bpjeps_abrev', e.target.value.toUpperCase())}
                  placeholder="MDA"
                  data-testid="input-bpjeps-abrev"
                />
                <p className="text-xs text-slate-500 mt-1">Ex: MDA (Motonautisme et Disciplines Associées)</p>
              </div>
              <div>
                <Label htmlFor="centre_abrev">Abréviation Centre</Label>
                <Input
                  id="centre_abrev"
                  type="text"
                  maxLength={5}
                  value={currentProfile.centre_abrev || ''}
                  onChange={(e) => handleChange('centre_abrev', e.target.value.toUpperCase())}
                  placeholder="ATL"
                  data-testid="input-centre-abrev"
                />
                <p className="text-xs text-slate-500 mt-1">Ex: ATL (Atlantique), OCC (Occitanie)</p>
              </div>
            </div>
          </div>
          
          {/* IBAN pour factures */}
          <div className="md:col-span-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Coordonnées Bancaires (Factures)
            </h4>
            <p className="text-xs text-amber-600 mb-3">Ces informations apparaîtront sur les factures générées.</p>
            <div>
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                type="text"
                value={currentProfile.iban || ''}
                onChange={(e) => handleChange('iban', e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                data-testid="input-iban"
              />
              <p className="text-xs text-slate-500 mt-1">Exemple: FR7630001007941234567890185</p>
            </div>
          </div>
          
          {/* Agenda Google */}
          <div className="md:col-span-2 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <h4 className="font-medium text-indigo-800 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Intégration Agenda Google
            </h4>
            <p className="text-xs text-indigo-600 mb-3">Code d'intégration de l'agenda Google Calendar pour ce centre. Ce code change chaque année.</p>
            <div>
              <Label htmlFor="google_calendar_code">Code d'intégration Google Calendar</Label>
              <Input
                id="google_calendar_code"
                type="text"
                value={currentProfile.google_calendar_code || ''}
                onChange={(e) => handleChange('google_calendar_code', e.target.value)}
                placeholder="ex: abc123def456@group.calendar.google.com"
                data-testid="input-google-calendar-code"
              />
              <p className="text-xs text-slate-500 mt-1">Collez ici le code d'intégration de votre agenda Google (visible dans les paramètres de partage du calendrier)</p>
            </div>
          </div>
          
          {/* Programme de formation PDF */}
          <div className="md:col-span-2 p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Programme de formation (PDF)
            </h4>
            <p className="text-xs text-green-600 mb-3">Ce fichier sera envoyé en pièce jointe lors de l'action "Envoyer programme de formation" dans le CRM.</p>
            
            {currentProfile.programme_pdf ? (
              <div className="flex items-center justify-between p-3 bg-white rounded border">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">{currentProfile.programme_pdf_name || 'Programme.pdf'}</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = currentProfile.programme_pdf;
                      link.download = currentProfile.programme_pdf_name || 'Programme.pdf';
                      link.click();
                    }}
                    data-testid="download-programme-btn"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleRemoveProgrammePdf}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="delete-programme-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center p-4 border-2 border-dashed border-green-300 rounded-lg">
                <Upload className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm text-green-600 mb-2">Aucun programme téléchargé</p>
                <input
                  type="file"
                  ref={programmeInputRef}
                  accept=".pdf"
                  onChange={handleProgrammePdfUpload}
                  className="hidden"
                />
                <Button 
                  variant="outline"
                  onClick={() => programmeInputRef.current?.click()}
                  className="border-green-400 text-green-700 hover:bg-green-100"
                  data-testid="upload-programme-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Télécharger un PDF
                </Button>
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="session">N° de session</Label>
            <Input
              id="session"
              value={currentProfile.session}
              onChange={(e) => handleChange('session', e.target.value)}
              placeholder="2024-A"
              data-testid="input-session"
            />
          </div>
          
          <div>
            <Label htmlFor="date_debut_formation">Date de début</Label>
            <Input
              id="date_debut_formation"
              type="date"
              value={currentProfile.date_debut_formation}
              onChange={(e) => handleChange('date_debut_formation', e.target.value)}
              data-testid="input-date-debut"
            />
          </div>
          
          <div>
            <Label htmlFor="date_fin_formation">Date de fin</Label>
            <Input
              id="date_fin_formation"
              type="date"
              value={currentProfile.date_fin_formation}
              onChange={(e) => handleChange('date_fin_formation', e.target.value)}
              data-testid="input-date-fin"
            />
          </div>
          
          <div>
            <Label htmlFor="horaires_matin">Horaires matin</Label>
            <Input
              id="horaires_matin"
              value={currentProfile.horaires_matin}
              onChange={(e) => handleChange('horaires_matin', e.target.value)}
              placeholder="09:00 - 12:00"
              data-testid="input-horaires-matin"
            />
          </div>
          
          <div>
            <Label htmlFor="horaires_apres_midi">Horaires après-midi</Label>
            <Input
              id="horaires_apres_midi"
              value={currentProfile.horaires_apres_midi}
              onChange={(e) => handleChange('horaires_apres_midi', e.target.value)}
              placeholder="14:00 - 17:00"
              data-testid="input-horaires-apres-midi"
            />
          </div>
        </div>
      </div>

      {/* Tampon / Signature du centre (pour les certificats) */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Stamp className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Tampon / Signature du Centre</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Ce tampon sera utilisé sur les certificats EPMSP et autres documents officiels.
        </p>
        
        <div className="flex items-start gap-6">
          <div className="w-48 h-32 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50" data-testid="tampon-preview">
            {currentProfile.tampon_img ? (
              <img src={currentProfile.tampon_img} alt="Tampon" className="max-w-full max-h-full object-contain p-2" />
            ) : (
              <span className="text-slate-400 text-sm text-center px-2">Aucun tampon</span>
            )}
          </div>
          <div>
            <input 
              type="file" 
              id="tampon-upload"
              accept="image/*" 
              onChange={handleTamponUpload} 
              className="hidden" 
            />
            <label htmlFor="tampon-upload">
              <Button type="button" variant="outline" className="cursor-pointer" onClick={() => document.getElementById('tampon-upload').click()} data-testid="upload-tampon-button">
                <Upload className="h-4 w-4 mr-2" />
                {currentProfile.tampon_img ? 'Changer le tampon' : 'Ajouter un tampon'}
              </Button>
            </label>
            {currentProfile.tampon_img && (
              <Button 
                type="button" 
                variant="ghost" 
                className="ml-2 text-red-500 hover:text-red-700"
                onClick={() => handleChange('tampon_img', null)}
              >
                Supprimer
              </Button>
            )}
            <p className="text-xs text-slate-400 mt-2">Format recommandé: PNG avec fond transparent</p>
          </div>
        </div>
      </div>

      {/* Aperçu pied de page PDF */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <p className="text-sm text-slate-600 mb-2">Aperçu pied de page des fiches de présence :</p>
        <p className="text-xs text-slate-500 text-center">
          {currentProfile.nom_organisme} - SIRET: {currentProfile.siret || '...'} - 
          Tél: {currentProfile.telephone || '...'} - Email: {currentProfile.email || '...'}
        </p>
      </div>
    </div>
  );
};

export default ProfilCentre;
